import React, { useState, useEffect } from 'react';
import { api } from '@/api/apiClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Download,
  FileSpreadsheet,
  Users,
  Filter
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { format } from 'date-fns';

const exportFields = [
  { id: 'first_name', label: 'First Name', category: 'Personal' },
  { id: 'last_name', label: 'Last Name', category: 'Personal' },
  { id: 'user_email', label: 'Email', category: 'Personal' },
  { id: 'phone', label: 'Phone', category: 'Personal' },
  { id: 'date_of_birth', label: 'Date of Birth', category: 'Personal' },
  { id: 'address', label: 'Address', category: 'Address' },
  { id: 'city', label: 'City', category: 'Address' },
  { id: 'state', label: 'State', category: 'Address' },
  { id: 'zip_code', label: 'ZIP Code', category: 'Address' },
  { id: 'job_title', label: 'Job Title', category: 'Employment' },
  { id: 'department', label: 'Department', category: 'Employment' },
  { id: 'hire_date', label: 'Hire Date', category: 'Employment' },
  { id: 'employment_status', label: 'Employment Status', category: 'Employment' },
  { id: 'annual_salary', label: 'Annual Salary', category: 'Payroll' },
  { id: 'benefits_budget', label: 'Benefits Budget', category: 'Payroll' },
  { id: 'profile_confirmed', label: 'Profile Confirmed', category: 'Status' },
  { id: 'enrollment_status', label: 'Enrollment Status', category: 'Status' },
];

export default function ExportCensus() {
  const [employees, setEmployees] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [products, setProducts] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployerId, setSelectedEmployerId] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedFields, setSelectedFields] = useState(
    exportFields.filter(f => ['first_name', 'last_name', 'user_email', 'job_title', 'enrollment_status'].includes(f.id)).map(f => f.id)
  );
  const [includeEnrollments, setIncludeEnrollments] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      const user = await api.auth.me();

      const systemAdmin = user?.user_role === 'admin' || user?.role === 'ADMIN';
      setIsSystemAdmin(systemAdmin);

      if (systemAdmin) {
        const employersData = await api.entities.Employer.list();
        setEmployers(employersData);
        const defaultEmployerId = employersData?.[0]?.id || '';
        setSelectedEmployerId(defaultEmployerId);
        if (defaultEmployerId) {
          await loadData(defaultEmployerId, true);
        } else {
          setEmployees([]);
          setEnrollments([]);
          setProducts([]);
        }
        return;
      }
      
      if (user.employer_id) {
        await loadData(user.employer_id, false);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (employerId, systemAdmin) => {
    const [employeesData, enrollmentsData, productsData] = await Promise.all([
      api.entities.Employee.filter({ employer_id: employerId }),
      api.entities.Enrollment.filter({ employer_id: employerId }),
      systemAdmin ? api.entities.BenefitProduct.filter({ employer_id: employerId }) : api.entities.BenefitProduct.list()
    ]);
    setEmployees(employeesData);
    setEnrollments(enrollmentsData);
    setProducts(productsData);
  };

  const handleEmployerChange = async (value) => {
    setSelectedEmployerId(value);
    setLoading(true);
    try {
      await loadData(value, true);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load census data');
    } finally {
      setLoading(false);
    }
  };

  const toggleField = (fieldId) => {
    setSelectedFields(prev => 
      prev.includes(fieldId) 
        ? prev.filter(f => f !== fieldId)
        : [...prev, fieldId]
    );
  };

  const selectAllFields = () => {
    setSelectedFields(exportFields.map(f => f.id));
  };

  const clearAllFields = () => {
    setSelectedFields([]);
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.id === productId);
    return product?.name || '';
  };

  const getEmployeeEnrollments = (employeeId) => {
    return enrollments
      .filter(e => e.employee_id === employeeId && e.status === 'active')
      .map(e => getProductName(e.product_id))
      .filter(Boolean);
  };

  const getTotalPremium = (employeeId) => {
    return enrollments
      .filter(e => e.employee_id === employeeId && e.status === 'active')
      .reduce((sum, e) => sum + (e.premium_amount || 0), 0);
  };

  const filteredEmployees = employees.filter(emp => {
    if (filterStatus === 'all') return emp.is_active !== false;
    if (filterStatus === 'enrolled') {
      const hasEnrollment = enrollments.some(e => e.employee_id === emp.id && e.status === 'active');
      return hasEnrollment && emp.is_active !== false;
    }
    if (filterStatus === 'not_enrolled') {
      const hasEnrollment = enrollments.some(e => e.employee_id === emp.id && e.status === 'active');
      return !hasEnrollment && emp.is_active !== false;
    }
    return emp.is_active !== false;
  });

  const handleExport = () => {
    if (selectedFields.length === 0) {
      toast.error('Please select at least one field to export');
      return;
    }

    // Build headers
    let headers = selectedFields.map(fId => {
      const field = exportFields.find(f => f.id === fId);
      return field?.label || fId;
    });

    if (includeEnrollments) {
      headers.push('Enrolled Benefits', 'Total Monthly Premium');
    }

    // Build rows
    const rows = filteredEmployees.map(emp => {
      let row = selectedFields.map(fId => {
        let value = emp[fId];
        if (value === true) value = 'Yes';
        if (value === false) value = 'No';
        if (value === null || value === undefined) value = '';
        // Escape commas and quotes for CSV
        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
          value = `"${value.replaceAll('"', '""')}"`;
        }
        return value;
      });

      if (includeEnrollments) {
        const benefits = getEmployeeEnrollments(emp.id).join('; ');
        const premium = getTotalPremium(emp.id).toFixed(2);
        row.push(`"${benefits}"`, premium);
      }

      return row.join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `employee_census_${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();

    toast.success(`Exported ${filteredEmployees.length} employees`);
  };

  const categories = [...new Set(exportFields.map(f => f.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Export Census</h1>
        <p className="text-slate-500 mt-1">
          Download employee data with enrollment information
        </p>
      </div>

      {isSystemAdmin && (
        <Card>
          <CardContent className="p-4">
            <div className="grid gap-2">
              <Label>Employer</Label>
              <Select value={selectedEmployerId} onValueChange={handleEmployerChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select employer" />
                </SelectTrigger>
                <SelectContent>
                  {employers.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.company_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary */}
      <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
        <CardContent className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-emerald-900">
                {filteredEmployees.length} Employees
              </p>
              <p className="text-sm text-emerald-700">
                Ready for export
              </p>
            </div>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700">
            {selectedFields.length} fields selected
          </Badge>
        </CardContent>
      </Card>

      {/* Filter Options */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-blue-500" />
            Filter Employees
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filterStatus === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('all')}
            >
              All Active ({employees.filter(e => e.is_active !== false).length})
            </Button>
            <Button
              variant={filterStatus === 'enrolled' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('enrolled')}
            >
              Enrolled Only
            </Button>
            <Button
              variant={filterStatus === 'not_enrolled' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterStatus('not_enrolled')}
            >
              Not Enrolled
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Field Selection */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-500" />
              Select Export Fields
            </CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllFields}>
                Select All
              </Button>
              <Button variant="outline" size="sm" onClick={clearAllFields}>
                Clear All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {categories.map(category => (
            <div key={category}>
              <p className="text-sm font-medium text-slate-500 mb-3">{category}</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {exportFields.filter(f => f.category === category).map(field => (
                  <div 
                    key={field.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                      selectedFields.includes(field.id) 
                        ? 'bg-emerald-50 border-emerald-200' 
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <Checkbox 
                      checked={selectedFields.includes(field.id)}
                      onCheckedChange={() => toggleField(field.id)}
                    />
                    <Label className="cursor-pointer">{field.label}</Label>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="pt-4 border-t">
            <div 
              className={`flex items-center gap-3 p-4 rounded-lg border transition-colors ${
                includeEnrollments 
                  ? 'bg-purple-50 border-purple-200' 
                  : 'bg-white border-slate-200 hover:bg-slate-50'
              }`}
            >
              <Checkbox 
                checked={includeEnrollments}
                onCheckedChange={setIncludeEnrollments}
              />
              <div>
                <Label className="cursor-pointer font-medium">Include Enrollment Data</Label>
                <p className="text-sm text-slate-500">Add enrolled benefits and premium amounts</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Button 
          onClick={handleExport}
          className="w-full h-14 text-lg bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
          disabled={selectedFields.length === 0}
        >
          <Download className="w-5 h-5 mr-2" />
          Export {filteredEmployees.length} Employees to CSV
        </Button>
      </motion.div>
    </div>
  );
}